/*
 * The Maker Map band's live-map phone (sections/dte-map-band.liquid). Plain script, no library.
 * Justified in docs/decisions-log.md (2026-09-30): the film must hold at set times and lines must be
 * measured from the page, which CSS alone cannot do.
 *
 * - Loads the film (no src until the page has loaded and the phone is within 400px of the view; if
 *   the phone comes into view first, it loads then), plays it muted and inline
 *   while the phone is at least half in view, pauses it out of view.
 * - At each note's time (data-at, seconds) the film holds about 2.5 s: a ring closes on the note's
 *   point on the film (data-x, data-y, percent), a hairline draws from it to the note, the note wipes
 *   in; then the line runs off into the note, the note dims and the film runs on.
 * - At the end the film holds on its end card and every note shows; the button plays it again.
 * - The button pauses and plays everything (WCAG 2.2.2).
 * - Reduced motion, Save-Data, or autoplay refused: no autoplay; the still and all the notes show, and
 *   the button plays the film straight through on request. Without this file: still plus notes.
 */
(function () {
  'use strict';

  var NS = 'http://www.w3.org/2000/svg';
  var HOLD = 2600; // ms the film waits at a note
  var DRAW = 700; // ms for the line to draw
  var OFF = 460; // ms for the line to run off into the note
  var EASE = 'cubic-bezier(0.6, 0, 0.2, 1)';

  function box(el, o) {
    var r = el.getBoundingClientRect();
    return { l: r.left - o.left, t: r.top - o.top, r: r.right - o.left, b: r.bottom - o.top, w: r.width, h: r.height };
  }

  function px(v) {
    return Math.round(v * 2) / 2;
  }

  // An orthogonal polyline with softly rounded corners. Zero-length legs are dropped.
  function route(pts, rad) {
    var p = [pts[0]];
    for (var i = 1; i < pts.length; i++) {
      var q = p[p.length - 1];
      if (Math.abs(pts[i][0] - q[0]) + Math.abs(pts[i][1] - q[1]) > 0.5) p.push(pts[i]);
    }
    var d = 'M' + p[0][0] + ' ' + p[0][1];
    for (var j = 1; j < p.length - 1; j++) {
      var a = p[j - 1], c = p[j], b = p[j + 1];
      var l1 = Math.hypot(c[0] - a[0], c[1] - a[1]);
      var l2 = Math.hypot(b[0] - c[0], b[1] - c[1]);
      var r = Math.min(rad, l1 / 2, l2 / 2);
      var s = [c[0] - ((c[0] - a[0]) / l1) * r, c[1] - ((c[1] - a[1]) / l1) * r];
      var e = [c[0] + ((b[0] - c[0]) / l2) * r, c[1] + ((b[1] - c[1]) / l2) * r];
      d += ' L' + s[0] + ' ' + s[1] + ' Q' + c[0] + ' ' + c[1] + ' ' + e[0] + ' ' + e[1];
    }
    var z = p[p.length - 1];
    return d + ' L' + z[0] + ' ' + z[1];
  }

  function init(opt) {
    var video = opt.querySelector('[data-mb-video]');
    if (!video || opt.dataset.mbReady) return;
    opt.dataset.mbReady = '1';

    var stage = opt.querySelector('[data-mb-stage]');
    var device = opt.querySelector('[data-mb-device]');
    var frame = opt.querySelector('[data-mb-frame]');
    var mark = opt.querySelector('[data-mb-mark]');
    var toggle = opt.querySelector('[data-mb-toggle]');
    var svg = opt.querySelector('[data-mb-leads]');

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var saveData = !!(navigator.connection && navigator.connection.saveData);
    var auto = !reduce && !saveData;

    var beats = Array.prototype.map
      .call(opt.querySelectorAll('[data-mb-note]'), function (el) {
        return {
          el: el,
          pip: el.querySelector('.dte-mb__pip'),
          at: parseFloat(el.dataset.at) || 0,
          x: parseFloat(el.dataset.x) || 50,
          y: parseFloat(el.dataset.y) || 50,
        };
      })
      .sort(function (a, b) {
        return a.at - b.at;
      });

    var seq = auto && beats.length > 0;
    var next = 0;
    var holding = -1;
    var holdLeft = 0;
    var holdStart = 0;
    var timer = 0;
    var raf = 0;
    var inView = false;
    var userPaused = !auto;
    var ended = false;
    var loaded = false;

    // The two strokes of one line: deep sea on the wattle band, wattle where it crosses the phone
    var id = 'dte-mb-clip-' + (opt.dataset.mbId || Math.random().toString(36).slice(2));
    var defs = document.createElementNS(NS, 'defs');
    var clip = document.createElementNS(NS, 'clipPath');
    clip.setAttribute('id', id);
    var clipRect = document.createElementNS(NS, 'rect');
    clip.appendChild(clipRect);
    defs.appendChild(clip);
    var pOut = document.createElementNS(NS, 'path');
    pOut.setAttribute('class', 'dte-mb__lead dte-mb__lead--out');
    var pIn = document.createElementNS(NS, 'path');
    pIn.setAttribute('class', 'dte-mb__lead dte-mb__lead--in');
    pIn.setAttribute('clip-path', 'url(#' + id + ')');
    svg.appendChild(defs);
    svg.appendChild(pOut);
    svg.appendChild(pIn);
    var lineLen = 0;
    var lineOn = false;

    function setDash(off, dur) {
      [pOut, pIn].forEach(function (p) {
        p.style.transition = dur ? 'stroke-dashoffset ' + dur + 'ms ' + EASE : 'none';
        p.style.strokeDasharray = lineLen + ' ' + (lineLen + 2);
        p.style.strokeDashoffset = off;
      });
    }

    // Measure the line for a beat from where things are now
    function layout(b) {
      var o = opt.getBoundingClientRect();
      var m = box(mark, o);
      var d = box(device, o);
      var pip = box(b.pip, o);
      var ax = m.l + m.w / 2;
      var ay = px(m.t + m.h / 2);
      var ring = m.w / 2;
      var sx = ax + ring; // leave the ring from its right side
      // Out to the right as far as the notes' rail, then along the rail into the note's stop (the rail
      // is beside the phone on phones, left of the notes from 750px: the same two legs either way)
      var cx = px(pip.l + pip.w / 2);
      var cy = pip.t + pip.h / 2;
      var pts;
      if (Math.abs(ay - cy) < pip.h) pts = [[sx, ay], [pip.l - 2, ay]];
      else pts = [[sx, ay], [cx, ay], [cx, ay < cy ? pip.t - 2 : pip.b + 2]];
      pOut.setAttribute('d', route(pts, 7));
      pIn.setAttribute('d', pOut.getAttribute('d'));
      var dr = parseFloat(getComputedStyle(device).borderTopLeftRadius) || 0;
      clipRect.setAttribute('x', d.l);
      clipRect.setAttribute('y', d.t);
      clipRect.setAttribute('width', d.w);
      clipRect.setAttribute('height', d.h);
      clipRect.setAttribute('rx', Math.min(dr, d.w / 2));
      lineLen = Math.ceil(pOut.getTotalLength()) + 1;
    }

    function drawLine(b) {
      mark.style.left = b.x + '%';
      mark.style.top = b.y + '%';
      layout(b);
      setDash(lineLen, 0);
      svg.getBoundingClientRect();
      requestAnimationFrame(function () {
        setDash(0, DRAW);
      });
      lineOn = true;
    }

    function clearLine(animate) {
      if (!lineOn) return;
      lineOn = false;
      setDash(-lineLen, animate ? OFF : 0);
    }

    function setToggle(state) {
      toggle.dataset.state = state;
      var label = state === 'pause' ? toggle.dataset.lPause : ended ? toggle.dataset.lAgain : toggle.dataset.lPlay;
      toggle.setAttribute('aria-label', label);
    }

    function load() {
      if (loaded) return;
      loaded = true;
      video.muted = true;
      video.src = video.dataset.src;
      video.preload = 'auto';
    }

    function tick() {
      raf = 0;
      if (video.paused) return;
      if (seq && next < beats.length && video.currentTime >= beats[next].at - 0.02) {
        hold(next);
        return;
      }
      raf = requestAnimationFrame(tick);
    }

    function startTimer() {
      clearTimeout(timer);
      holdStart = performance.now();
      timer = setTimeout(release, holdLeft);
    }

    function stopTimer() {
      if (!timer) return;
      clearTimeout(timer);
      timer = 0;
      holdLeft = Math.max(0, holdLeft - (performance.now() - holdStart));
    }

    function hold(i) {
      video.pause();
      holding = i;
      holdLeft = HOLD;
      beats[i].el.classList.add('is-on');
      opt.classList.add('is-holding');
      drawLine(beats[i]);
      startTimer();
    }

    function release() {
      timer = 0;
      var b = beats[holding];
      b.el.classList.remove('is-on');
      b.el.classList.add('is-done');
      opt.classList.remove('is-holding');
      clearLine(true);
      holding = -1;
      next++;
      run(false);
    }

    // Carry on with whatever is due, if nothing says stop
    function run(force) {
      if (ended || userPaused || (!inView && !force)) return;
      if (holding >= 0) {
        startTimer();
        return;
      }
      load();
      var p = video.play();
      if (p && p.catch) {
        p.catch(function (err) {
          if (err && err.name === 'NotAllowedError') refused();
        });
      }
    }

    // Autoplay refused (Low Power Mode and the like): the static state, and the button to play
    function refused() {
      seq = false;
      opt.classList.remove('is-seq');
      userPaused = true;
      setToggle('play');
    }

    function suspend() {
      if (!video.paused) video.pause();
      stopTimer();
    }

    function restartSeq() {
      next = 0;
      holding = -1;
      clearTimeout(timer);
      timer = 0;
      clearLine(false);
      opt.classList.remove('is-holding', 'is-end');
      beats.forEach(function (b) {
        b.el.classList.remove('is-on', 'is-done');
      });
    }

    video.addEventListener('playing', function () {
      opt.classList.add('is-rolling');
      if (!raf) raf = requestAnimationFrame(tick);
    });

    video.addEventListener('ended', function () {
      ended = true;
      beats.forEach(function (b) {
        b.el.classList.remove('is-on');
        b.el.classList.add('is-done');
      });
      opt.classList.add('is-end');
      setToggle('play');
    });

    toggle.addEventListener('click', function () {
      if (ended) {
        // Play again from the start
        ended = false;
        if (seq) restartSeq();
        opt.classList.remove('is-end');
        video.currentTime = 0;
        userPaused = false;
        setToggle('pause');
        run(true);
        return;
      }
      if (userPaused) {
        // A viewer who never had autoplay gets the sequence if the film is still at the start
        if (!seq && !reduce && !saveData && beats.length && video.currentTime < 0.1) {
          seq = true;
          opt.classList.add('is-seq');
          restartSeq();
        }
        userPaused = false;
        setToggle('pause');
        run(true);
      } else {
        userPaused = true;
        suspend();
        setToggle('play');
      }
    });

    if (seq) opt.classList.add('is-seq');
    setToggle(auto ? 'pause' : 'play');
    toggle.hidden = false;

    if ('IntersectionObserver' in window) {
      // Fetch the film only after the page has loaded (never competing with the hero), and only
      // once the phone is near the view
      var near = new IntersectionObserver(
        function (es) {
          if (es[0].isIntersecting) {
            if (auto) load();
            near.disconnect();
          }
        },
        { rootMargin: '400px 0px' }
      );
      if (document.readyState === 'complete') near.observe(stage);
      else
        window.addEventListener('load', function () {
          near.observe(stage);
        });

      var vis = new IntersectionObserver(
        function (es) {
          inView = es[0].intersectionRatio >= 0.5;
          if (inView) run(false);
          else suspend();
        },
        { threshold: [0, 0.5, 1] }
      );
      vis.observe(stage);
    } else {
      inView = true;
    }

    // Keep a drawn line on its note if the layout moves (fonts, rotation, resize)
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        if (lineOn && holding >= 0) {
          layout(beats[holding]);
          setDash(0, 0);
        }
      }).observe(opt);
    }
  }

  function start(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-mb-live]'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { start(); });
  else start();

  document.addEventListener('shopify:section:load', function (e) {
    start(e.target);
  });
})();
